/**
 * FAQ Service
 *
 * Handles API calls for FAQ content.
 * Used when REACT_APP_USE_CMS_CONTENT is enabled.
 *
 * Follows the established pattern:
 * - Feature flag check
 * - API call to main backend
 * - Fallback to hardcoded data on failure
 *
 * Backend API Documentation: docs/cms_product_implementation/docs/19-faq-api-schema-documentation.md
 */

import api from './api';
import featureFlags from '../utils/featureFlags';

const faqService = {
  /**
   * Get all FAQ sections from backend
   *
   * Returns FAQ sections with nested questions, ordered by displayOrder.
   * Falls back to hardcoded data if:
   * - Feature flag is disabled
   * - API call fails
   * - Network error occurs
   *
   * @returns {Promise<object>} - Response object
   * @returns {boolean} response.success - Whether API call succeeded
   * @returns {Array} response.data - Array of FAQ sections (if successful)
   * @returns {string} response.timestamp - ISO 8601 timestamp (if successful)
   * @returns {boolean} response.useHardcoded - Flag indicating fallback needed
   * @returns {string} response.error - Error message (if failed)
   * @returns {string} response.reason - Reason for fallback (if feature flag disabled)
   *
   * @example
   * const result = await faqService.getAllFAQSections();
   * if (result.success) {
   *   console.log('FAQ sections:', result.data);
   * } else if (result.useHardcoded) {
   *   console.log('Using hardcoded fallback:', result.reason || result.error);
   * }
   */
  async getAllFAQSections() {
    // Check feature flag first
    if (!featureFlags.useCmsContent()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[faqService] CMS content disabled via feature flag');
      }
      return {
        success: false,
        useHardcoded: true,
        reason: 'Feature flag disabled (REACT_APP_USE_CMS_CONTENT=false)'
      };
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[faqService] Fetching FAQ sections from API...');
      }

      const response = await api.get('/faq');

      if (process.env.NODE_ENV === 'development') {
        console.log('[faqService] FAQ sections loaded from API:', {
          sections: response.data.data?.length || 0,
          timestamp: response.data.timestamp
        });
      }

      return {
        success: true,
        data: response.data.data,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      console.error('[faqService] Error fetching FAQ sections:', error);

      // Return failure with fallback flag
      return {
        success: false,
        error: error.message || 'Failed to fetch FAQ sections',
        useHardcoded: true
      };
    }
  },

  /**
   * Get single FAQ section by slug
   *
   * @param {string} slug - Section slug (e.g., 'general', 'ordering', 'products')
   * @returns {Promise<object>} - Response object
   * @returns {boolean} response.success - Whether API call succeeded
   * @returns {object} response.data - FAQ section object (if successful)
   * @returns {string} response.timestamp - ISO 8601 timestamp (if successful)
   * @returns {boolean} response.useHardcoded - Flag indicating fallback needed
   * @returns {string} response.error - Error message (if failed)
   *
   * @example
   * const result = await faqService.getFAQSection('general');
   * if (result.success) {
   *   console.log('Section:', result.data.title);
   *   console.log('Questions:', result.data.questions.length);
   * }
   */
  async getFAQSection(slug) {
    // Check feature flag
    if (!featureFlags.useCmsContent()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[faqService] CMS content disabled via feature flag');
      }
      return {
        success: false,
        useHardcoded: true,
        reason: 'Feature flag disabled'
      };
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[faqService] Fetching FAQ section: ${slug}`);
      }

      const response = await api.get(`/faq/${slug}`);

      if (process.env.NODE_ENV === 'development') {
        console.log(`[faqService] Section "${slug}" loaded from API`);
      }

      return {
        success: true,
        data: response.data.data,
        timestamp: response.data.timestamp
      };
    } catch (error) {
      console.error(`[faqService] Error fetching FAQ section "${slug}":`, error);

      // Handle 404 specifically
      if (error.response?.status === 404) {
        return {
          success: false,
          error: `FAQ section not found: ${slug}`,
          useHardcoded: true
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to fetch FAQ section',
        useHardcoded: true
      };
    }
  },

  /**
   * Check if a FAQ section exists (without fetching full data)
   *
   * @param {string} slug - Section slug to check
   * @returns {Promise<boolean>} - True if section exists and is published
   *
   * @example
   * const exists = await faqService.sectionExists('general');
   * if (exists) {
   *   console.log('Section exists');
   * }
   */
  async sectionExists(slug) {
    // Check feature flag
    if (!featureFlags.useCmsContent()) {
      return false;
    }

    try {
      const response = await api.get(`/faq/${slug}/exists`);
      return response.data.data === true;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[faqService] Error checking if section "${slug}" exists:`, error);
      }
      return false;
    }
  }
};

export default faqService;
